<template>
  <div>
    <div
      v-if="menu.loadStatus === 'error'"
      class="section"
    >
      <p class="message message--danger">
        Oops! We weren't able to load the menu. Please try again later or select a different location.
      </p>
    </div>

    <div v-else>
      <menu-list-filter
        v-model="searchTerm"
        :is-read-only="isReadOnly"
        :is-search-mode-active="isSearchModeActive"
        @set-search-mode="handleSearchModeChange"
        @scroll-to-category="handleOpenCategoryClick"
      />

      <slot name="menu-list-header-banner" />

      <menu-list-filter-status v-if="hasSelectedMenuFilters" />

      <menu-search-filter-results
        v-if="isSearchModeActive && searchTerm"
        :is-read-only="isReadOnly"
        :search-term="searchTerm"
        :search-results="menuItemSearchResults"
        @open-menu-item="handleOpenMenuItemClick"
      />

      <div
        v-else
        :class="['menu-list menu-list--default', { 'menu-list--read-only': isReadOnly }]"
      >
        <menu-list-category
          v-for="(category, categoryIndex) in menu.filteredCategories"
          :key="category.categoryId"
          :category="category"
          :category-index="categoryIndex"
          :open-category-ids="openCategoryIds"
          :is-read-only="isReadOnly"
          @open-menu-list-category="handleOpenCategoryClick"
          @open-menu-item="handleOpenMenuItemClick"
        />
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import { generateCategoryHash, setBrowserHash } from '@/helpers/menu-hash-helper';

  import MenuListFilter from './components/menu-list-filter.vue';
  import MenuListFilterStatus from './components/menu-list-filter-status.vue';
  import MenuSearchFilterResults from './components/menu-search-filter-results.vue';
  import MenuListCategory from './components/menu-list-category.vue';


  export default {
    name: 'MenuList',

    components: {
      MenuListFilter,
      MenuListFilterStatus,
      MenuSearchFilterResults,
      MenuListCategory
    },

    props: {
      itemRouteName: {
        type: String,
        default: null
      },

      isReadOnly: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        openCategoryIds: [],
        isSearchModeActive: false,
        searchTerm: ''
      };
    },

    computed: {
      ...mapGetters('favoriteItems', ['favoriteItems']),
      ...mapGetters('menu', ['menu', 'filterAttributes']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('session', ['orderDetails', 'session']),

      hasSelectedMenuFilters() {
        return this.filterAttributes.selected.length > 0;
      },

      menuItemSearchResults() {
        const results = [];
        const resultMenuItemIds = [];
        const searchTerm = this.sanitizeString(this.searchTerm);

        this.menu.filteredCategories.forEach((category) => {
          if (category.categoryTemplate === 'FavoritesCategoryTemplate') return;

          category.menuItems.forEach((menuItem) => {
            const menuItemDisplayName = this.sanitizeString(menuItem.displayName);

            const menuItemDescription = this.sanitizeString(menuItem.descriptionText);

            const isSearchTagMatch = this.checkIsSearchTagMatch(menuItem, searchTerm, resultMenuItemIds);

            const isMenuItemNameMatch = menuItemDisplayName.indexOf(searchTerm) > -1 && resultMenuItemIds.indexOf(menuItem.menuItemId) === -1;

            const isMenuItemDescriptionMatch = menuItemDescription?.indexOf(searchTerm) > -1 && resultMenuItemIds.indexOf(menuItem.menuItemId) === -1;

            if (isMenuItemNameMatch || isMenuItemDescriptionMatch || isSearchTagMatch) {
              results.push(menuItem);

              resultMenuItemIds.push(menuItem.menuItemId);
            }
          });
        });

        return results;
      }
    },

    mounted() {
      this.onComponentMounted();
    },

    methods: {
      onComponentMounted() {
        this.verifyAgeForAlcohol();

        this.$store.dispatch('merchant/openInAppMessage', { type: 'Menu' });

        this.$store.dispatch('favoriteItems/pruneUnfavoritedItems');

        if (this.menu.categories.length === 1 || this.merchant.options.defaultCategoriesExpanded) {
          this.openCategoryIds = this.menu.categories.map(c => c.categoryId);
        }
      },

      handleSearchModeChange(status) {
        this.isSearchModeActive = status;

        if (!this.isSearchModeActive) this.searchTerm = '';
      },

      handleClearFiltersClick() {
        this.$store.dispatch('menu/updateSelectedFilters', []);
      },

      handleOpenCategoryClick({ categoryId, doNotClose }) {
        const category = this.menu.filteredCategories.find(cat => cat.categoryId === categoryId);

        if (category && category.displayName) {
          const categoryHash = generateCategoryHash(category.displayName);
          setBrowserHash(categoryHash);
        }

        const categoryIndex = this.openCategoryIds.indexOf(categoryId);
        const categoryWasOpen = categoryIndex > -1;

        if (doNotClose && categoryWasOpen) {
          return;
        }

        if (categoryWasOpen) {
          this.openCategoryIds.splice(categoryIndex, 1);
        }

        else {
          this.openCategoryIds.push(categoryId);
        }
      },

      handleOpenMenuItemClick(item) {
        const isReadOnlyViewItemEnabled = this.merchant.features['read-only-view-item-details-enabled'];

        const readOnlyViewItemUrlOverride = this.$route.query.dmb === 'true';

        if (item.ageVerificationTemplate === 'AlcoholTemplate' && !this.session.hasVerifiedAgeForAlcohol) {
          this.$openModal('ageVerificationModal', {
            onConfirm: () => this.handleOpenMenuItemClick(item),
            required: false
          });

          return;
        }

        if (this.itemRouteName) {
          this.$router.push({
            name: this.itemRouteName,
            params: { merchantId: this.$route.params.merchantId, menuItemId: item.menuItemId },
            query: { categoryId: this.categoryId, favoriteItemId: item.favoriteItemId }
          });
        }

        else {
          this.$store.dispatch('menu/selectItem', { favoriteItemId: item.favoriteItemId, itemId: item.menuItemId });

          this.$store.dispatch('menu/selectCategory', this.categoryId);

          if (!this.isReadOnly) {
            this.$openModal('menuItem');
          }
          else if (isReadOnlyViewItemEnabled && !readOnlyViewItemUrlOverride) {
            this.$openModal('read-only-menu-item-modal', { isReadOnly: true });
          }
        }
      },

      verifyAgeForAlcohol() {
        if (this.merchant.features['age-restricted'] && !this.session.hasVerifiedAgeForAlcohol) {
          this.$openModal('ageVerificationModal', { required: true });
        }
      },

      checkIsSearchTagMatch(menuItem, searchTerm, resultMenuItemIds) {
        const isMatch = menuItem.attributes.some((item) => {
          const isSearchTag = item.type === 'SearchTag';
          const itemTagName = this.sanitizeString(item.name);

          return isSearchTag && itemTagName.indexOf(searchTerm) > -1 && resultMenuItemIds.indexOf(menuItem.menuItemId) === -1;
        });

        return isMatch;
      },

      sanitizeString(str) {
        return str?.toLowerCase().replace(/[^a-zA-Z0-9\s]/, '').trim();
      }
    }
  };
</script>
